import type { Party, PartyInput, PartyListResponse } from '@/api/generated/model';
import {
  createParty,
  getGetAllPartiesQueryKey,
  useGetAllParties,
} from '@/api/generated/parties/parties';
import { PartyFormPanel } from '@/components/parties/PartyEditDialog';
import { DetailPreviewContext } from '@/contexts/DetailPreviewContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { useLanguage } from '@/i18n/language-hooks';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';

interface UsePartyCreateDialogOptions {
  onPartyCreated?: (partyId: string) => void;
}

function useDetailPreviewActions() {
  const context = useContext(DetailPreviewContext);
  return {
    openPreview: context?.openPreview ?? (() => undefined),
    closePreview: context?.closePreview ?? (() => undefined),
  };
}

export function usePartyCreateDialog(options: UsePartyCreateDialogOptions = {}) {
  const { onPartyCreated } = options;
  const { t } = useLanguage();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { openPreview, closePreview } = useDetailPreviewActions();
  const queryClient = useQueryClient();
  const { data: partiesResponse, refetch: refetchParties } = useGetAllParties();
  const parties = partiesResponse?.items ?? [];

  const handlePartyCreated = useCallback(
    async (newParty: Party) => {
      queryClient.setQueryData(
        getGetAllPartiesQueryKey(),
        (current: PartyListResponse | undefined): PartyListResponse => {
          if (!current) {
            return {
              items: [newParty],
              meta: { page: 0, limit: 50, total: 1, hasMore: false },
            };
          }
          const alreadyListed = current.items.some((party) => party.id === newParty.id);
          if (alreadyListed) {
            return current;
          }
          return {
            items: [newParty, ...current.items],
            meta: {
              ...current.meta,
              total: current.meta.total + 1,
            },
          };
        }
      );
      await queryClient.invalidateQueries({ queryKey: getGetAllPartiesQueryKey() });
      await refetchParties();
      onPartyCreated?.(newParty.id);
    },
    [onPartyCreated, queryClient, refetchParties]
  );

  const openCreateParty = useCallback(() => {
    if (isMobile) {
      void navigate('/settings/parties/new');
      return;
    }
    openPreview({
      title: t('settings.parties.add'),
      subtitle: t('settings.parties.description'),
      content: (
        <PartyFormPanel
          onSubmit={async (input: PartyInput) => {
            const newParty = await createParty(input);
            await handlePartyCreated(newParty);
            closePreview();
          }}
          onCancel={() => closePreview()}
        />
      ),
      onClose: () => closePreview(),
    });
  }, [closePreview, handlePartyCreated, isMobile, navigate, openPreview, t]);

  return {
    parties,
    openCreateParty,
    /** @deprecated Use openCreateParty — kept for EntitySelect onAddNew call sites */
    setDialogOpen: openCreateParty,
  };
}
